'use client';

import { parse } from 'url';
import React, { useEffect, useRef, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import { useSearchParams } from 'next/navigation';
import { useSession as useAuthSession } from 'next-auth/react';
import { Redirect } from '@web/router';
import { LoadingView } from '@web/components';
import { useSessionCustomer } from '../../customer';
import { useSyncPathToMachine } from '../useSyncPathToMachine';
import { onboardingInitiated, onboardingBackButtonClicked } from '../events';
import { translateContentToProps } from '../translation';
import { useReporter } from '../../reporter';
import { CrunchingNumbers } from '../CrunchingNumbers';
import { OnboardingProvider, OnboardingContext } from './context';
import { getActionFromKey, getKeyFromPath } from './functions';
import {
  buildRouteChangePayload,
  isLegacyPupInfoRoute,
  PLAN_INTERSTITIAL_STATE,
  resolvePlanInterstitialState,
  resolvePrefetchLoadingVariant,
  shouldRenderPrefetchLoading,
  shouldSkipInterstitialFromPath,
} from './helpers';
import { components } from './components';
import { ROOT_SUBPATH, pathToAction, STATE, unmappedStates } from './constants';

const OnboardingFlow = ({ content }) => {
  const {
    pet_id,
    pet,
    pets,
    customer,
    cart,
    send,
    stateKey,
    fromQuestion,
    petIsLoading,
    isCombinationsLoading,
    isCombinationsValidating,
    service,
    setShowPlanSelection,
    showPlanSelection,
    editPetFlow,
  } = useContext(OnboardingContext);

  const isLoading =
    petIsLoading || isCombinationsLoading || isCombinationsValidating;
  const reporter = useReporter();
  const shouldSkipPlanLoader =
    Boolean(editPetFlow?.current) || Boolean(pet.starter_box);
  const [animationDone, setAnimationDone] = useState(false);

  const router = useRouter();
  const searchParams = useSearchParams();
  const [subpath, slug] = router.query.path ?? [];
  const pathname = slug ? `${subpath}/${slug}` : subpath;
  const lastComponentRef = useRef({ component: LoadingView, props: {} });
  const routeChanging = useRef(false);
  const skipInterstitialRef = useRef(false);
  if (shouldSkipPlanLoader) {
    skipInterstitialRef.current = true;
  }
  let props = translateContentToProps(content);
  // Only fire analytics tag once on first render
  useEffect(() => {
    reporter.tag(onboardingInitiated());
  }, []); //eslint-disable-line react-hooks/exhaustive-deps

  // useEffect(() => {
  //   // this is forcing LoadingView to render and grant plansApi.isValidating
  //   // time to process, which fixes the double-triple+ rendering/analytics tag
  //   // firing for the default render of this component
  //   // (which contains FreshRecipesSelectionForm)
  //   // please note that this fix is ONLY for when going backwards
  //   // (Back in browser or clicking Edit) from CheckoutContainer

  //   if (skipInterstitialRef.current && !showPlanSelection) {
  //     setTimeout(() => {
  //       setShowPlanSelection(true);
  //     }, 100);
  //   }
  // }, [showPlanSelection]);

  useSyncPathToMachine(
    routeChanging,
    false,
    stateKey,
    unmappedStates,
    pet_id,
    setShowPlanSelection,
    fromQuestion,
    setAnimationDone,
  );

  // Router event handlers
  useEffect(() => {
    // When the URL changes via a router initiated change, change the state machine step to match the new URL
    // TODO - Revisit this functionality post launch given Add Pet and Edit Pet no longer live in the state machine
    const syncMachineToPath = (url, { browserInitiated }) => {
      routeChanging.current = true;
      const parsedUrl = parse(url);

      const key = getKeyFromPath(parsedUrl.pathname, ROOT_SUBPATH);

      if (stateKey !== key) {
        let action = pathToAction[key];

        // Handling browser back button
        if (browserInitiated) action = getActionFromKey(key, service);

        // Add pup link click
        if (isLegacyPupInfoRoute(key)) {
          action &&
            send(
              action(
                buildRouteChangePayload({
                  key,
                  routerAsPath: router.asPath,
                  pets,
                  pet_id,
                  pet,
                  cart,
                  customer,
                }),
              ),
            );
        } else {
          const searchParams = new URLSearchParams(parsedUrl.query);
          action &&
            send(
              action(
                buildRouteChangePayload({
                  key,
                  routerAsPath: router.asPath,
                  pets,
                  pet_id,
                  pet,
                  cart,
                  customer,
                  queryPetId: searchParams.get('pet_id'),
                }),
              ),
            );
        }
      }
    };

    const trackPreviousUrl = () => {
      const currentPath = router.asPath;
      // If we're going from Checkout -> Plan Selection
      // we should skip the interstitial. Using a ref
      // ensures we don't trigger unnecessary renders.
      skipInterstitialRef.current = shouldSkipInterstitialFromPath(currentPath);
    };

    router.events.on('routeChangeStart', syncMachineToPath);
    router.events.on('beforeHistoryChange', trackPreviousUrl);
    // When user hits the browser "back" button, change the state machine step to match the new URL
    router.beforePopState(({ as }) => {
      syncMachineToPath(as, { browserInitiated: true });
      reporter.tag(onboardingBackButtonClicked(stateKey));
      return true;
    });

    return () => {
      router.events.off('routeChangeStart', syncMachineToPath);
      router.events.off('beforeHistoryChange', trackPreviousUrl);
      router.beforePopState(() => true);
    };
  }, [
    cart,
    customer,
    pet,
    pet_id,
    pets,
    reporter,
    router,
    searchParams,
    send,
    service,
    stateKey,
  ]);

  useEffect(() => {
    if (!isLoading && animationDone && !showPlanSelection) {
      setShowPlanSelection(true);
    }
  }, [isLoading, animationDone, showPlanSelection, setShowPlanSelection]);

  //reset animation
  useEffect(() => {
    const planInterstitialState = resolvePlanInterstitialState({
      stateKey,
      pathname,
      isLoading,
      skipInterstitial: skipInterstitialRef.current,
      showPlanSelection,
    });

    if (planInterstitialState === PLAN_INTERSTITIAL_STATE.crunching) {
      setAnimationDone(false);
    }
  }, [stateKey, pathname, skipInterstitialRef, showPlanSelection, isLoading]);

  if (
    shouldRenderPrefetchLoading({ propsComponent: props.component, stateKey })
  ) {
    const { variant, petName } = resolvePrefetchLoadingVariant({
      pathname,
      petName: pet?.name,
    });

    if (variant === 'deletePet') {
      return <LoadingView variant="deletePet" petName={petName} />;
    }

    if (variant === 'initial') {
      return <LoadingView variant="initial" />;
    }

    return <LoadingView />;
  }

  let Component = components[stateKey];

  if ((pathname !== stateKey || !Component) && stateKey !== STATE.initial) {
    Component = lastComponentRef.current.component;
    props = lastComponentRef.current.props;
  } else lastComponentRef.current = { component: Component, props };

  const planInterstitialState = resolvePlanInterstitialState({
    stateKey,
    pathname,
    isLoading,
    skipInterstitial: skipInterstitialRef.current,
    showPlanSelection,
  });

  if (planInterstitialState === PLAN_INTERSTITIAL_STATE.loading) {
    return <LoadingView />;
  }

  if (planInterstitialState === PLAN_INTERSTITIAL_STATE.crunching) {
    const { content: crunchContent } = props.crunchingNumbersContentVariant;
    return (
      <CrunchingNumbers
        loadingBarBgColor={'background.lightest'}
        headline={crunchContent.headline}
        ollieLogo={props.ollieLogo}
        images={crunchContent.images}
        captions={crunchContent.captions}
        slideDuration={crunchContent.slideDuration}
        backgroundColor={crunchContent.backgroundColor}
        fontColor={crunchContent.fontColor}
        onLoadingComplete={() => setAnimationDone(true)}
      />
    );
  }

  return <Component {...props} />;
};

OnboardingFlow.displayName = 'OnboardingFlow';

OnboardingFlow.propTypes = {
  content: PropTypes.object.isRequired,
  component: PropTypes.string,
  noRecipesInfo: PropTypes.object,
  planLoaderMessages: PropTypes.arrayOf(PropTypes.object),
  planLoaderButtonText: PropTypes.string,
  body: PropTypes.arrayOf(
    PropTypes.shape({
      _uid: PropTypes.string,
    }),
  ),
};

// ************************

export const OnboardingFlowContainer = ({ content }) => {
  const { data: session, status } = useAuthSession();
  const isShopifyAuthenticated = session && status === 'authenticated';
  const { isActive, isLoading: isCustomerLoading } = useSessionCustomer();

  if (isCustomerLoading) {
    return <LoadingView />;
  }

  if (isShopifyAuthenticated && isActive) {
    return <Redirect to="/account/" />;
  }

  return (
    <div data-testid="onboarding-root">
      <OnboardingProvider>
        <OnboardingFlow content={content} />
      </OnboardingProvider>
    </div>
  );
};

OnboardingFlowContainer.displayName = 'OnboardingFlowContainer';

OnboardingFlowContainer.propTypes = {
  content: PropTypes.object.isRequired,
};
